/**
 * Modale d'import depuis Keycloak.
 * Responsabilité: orchestrer la recherche (2 endpoints) et l'import (création utilisateur Lauria),
 * sans coupler la logique au listing principal.
 */

import { ModalBody } from 'core/components/common/ModalBody'
import { ModalHeader } from 'core/components/common/ModalHeader'
import ModalWrapper from 'core/components/common/ModalWrapper'
import { toast } from 'core/components/common/Toast'
import {
  useLazySearchKeycloakComptesServiceQuery,
  useLazySearchKeycloakUtilisateursQuery,
} from 'lauria/api/keycloakImport'
import { useCreateUtilisateurMutation } from 'lauria/api/utilisateurs'
import { KeycloakImportResultsTable } from 'lauria/features/keycloakImport/KeycloakImportResultsTable'
import {
  KeycloakImportSearchForm,
  type KeycloakImportSearchType,
} from 'lauria/features/keycloakImport/KeycloakImportSearchForm'
import type { KeycloakImportItem } from 'lauria/types/keycloakImport'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

type SearchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'success'; items: KeycloakImportItem[] }

function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error

  if (error && typeof error === 'object') {
    const maybeRecord = error as Record<string, unknown>
    const data = maybeRecord.data

    if (typeof data === 'string') return data

    if (data && typeof data === 'object') {
      const dataRecord = data as Record<string, unknown>
      const detail = dataRecord.detail
      if (typeof detail === 'string') return detail

      const message = dataRecord.message
      if (typeof message === 'string') return message
    }

    const msg = maybeRecord.message
    if (typeof msg === 'string') return msg
  }

  return 'Erreur inattendue.'
}

function getImportErrorLabel(error: unknown): string {
  const { t } = useTranslation()

  if (error && typeof error === 'object') {
    const maybeRecord = error as Record<string, unknown>
    const data = maybeRecord.data

    if (data && typeof data === 'object') {
      const dataRecord = data as Record<string, unknown>
      const detail = dataRecord.detail
      const status = dataRecord.status

      if (status === 409 && detail === 'utilisateur_client_already_exists') {
        return t('profiles.importKeycloak.userAlreadyExists')
      }

      if (typeof detail === 'string' && detail.trim() !== '') {
        return detail
      }
    }
  }

  return getErrorMessage(error)
}

function buildCreateUtilisateurInput(item: KeycloakImportItem) {
  if (item.type === 'COMPTE_SERVICE') {
    return {
      idKeycloak: item.id,
      nomUtilisateur: item.identifier,
      compteService: true as const,
    }
  }

  return {
    idKeycloak: item.id,
    nomUtilisateur: item.identifier,
    nom: item.lastName ?? '',
    prenom: item.firstName ?? '',
    email: item.email ?? null,
    compteService: false as const,
  }
}

type KeycloakImportModalProps = {
  open: boolean
  onClose: () => void
  idClient: number
  onImported: () => void
}

export const KeycloakImportModal: React.FC<KeycloakImportModalProps> = ({
  open,
  onClose,
  idClient,
  onImported,
}) => {
  const { t } = useTranslation()
  const [searchInput, setSearchInput] = useState<string>('')
  const [searchType, setSearchType] =
    useState<KeycloakImportSearchType>('UTILISATEUR')
  const [state, setState] = useState<SearchState>({ kind: 'idle' })
  const [importingIds, setImportingIds] = useState<Set<string>>(() => new Set())

  /**
   * Etat par ligne:
   * - error → afficher message d'erreur
   * - success → afficher message de succès
   */
  const [importStatusById, setImportStatusById] = useState<
    Record<string, { type: 'error' | 'success'; message: string }>
  >({})

  const [triggerUsers] = useLazySearchKeycloakUtilisateursQuery()
  const [triggerServiceAccounts] = useLazySearchKeycloakComptesServiceQuery()
  const [createUtilisateur] = useCreateUtilisateurMutation()

  const canSearch = useMemo(() => searchInput.trim().length > 0, [searchInput])

  const onSearch = useCallback(async () => {
    const search = searchInput.trim()
    if (search === '') return

    setState({ kind: 'loading' })

    /**
     * On réinitialise les erreurs d'import à chaque nouvelle recherche,
     * pour repartir d'un état propre cohérent avec les résultats affichés.
     */
    setImportStatusById({})

    try {
      if (searchType === 'UTILISATEUR') {
        const users = await triggerUsers({ search }).unwrap()
        setState({ kind: 'success', items: users })
        return
      }

      const serviceAccounts = await triggerServiceAccounts({ search }).unwrap()
      setState({ kind: 'success', items: serviceAccounts })
    } catch (e) {
      setState({ kind: 'error', message: getErrorMessage(e) })
    }
  }, [searchInput, searchType, triggerUsers, triggerServiceAccounts])

  const onImport = useCallback(
    async (item: KeycloakImportItem) => {
      if (importingIds.has(item.id)) return
      if (importStatusById[item.id]) return

      setImportingIds((prev) => {
        const next = new Set(prev)
        next.add(item.id)
        return next
      })

      // nettoyage état précédent
      setImportStatusById((prev) => {
        if (!prev[item.id]) return prev
        const next = { ...prev }
        delete next[item.id]
        return next
      })

      try {
        const body = buildCreateUtilisateurInput(item)
        await createUtilisateur({ idClient, ...body }).unwrap()

        /**
         * 👉 Succès: on remplace le bouton par un message
         */
        setImportStatusById((prev) => ({
          ...prev,
          [item.id]: {
            type: 'success',
            message: t('profiles.importKeycloak.importSuccessInline'),
          },
        }))

        toast.success(t('profiles.importKeycloak.importSuccess'))
        onImported()
      } catch (e) {
        const errorLabel = getImportErrorLabel(e)

        setImportStatusById((prev) => ({
          ...prev,
          [item.id]: {
            type: 'error',
            message: errorLabel,
          },
        }))

        toast.error(errorLabel)
      } finally {
        setImportingIds((prev) => {
          const next = new Set(prev)
          next.delete(item.id)
          return next
        })
      }
    },
    [createUtilisateur, idClient, importingIds, importStatusById, onImported, t]
  )

  const content = useMemo(() => {
    if (state.kind === 'idle') {
      return (
        <div className="text-base text-gray-600 dark:text-gray-1250">
          {t('profiles.importKeycloak.help')}
        </div>
      )
    }

    if (state.kind === 'loading') {
      return (
        <div className="py-8 text-center text-gray-600 dark:text-gray-1250">
          {t('profiles.importKeycloak.loading')}
        </div>
      )
    }

    if (state.kind === 'error') {
      return (
        <div className="py-6">
          <div className="text-red-600 text-sm">{state.message}</div>
        </div>
      )
    }

    if (state.items.length === 0) {
      return (
        <div className="py-8 text-center text-gray-600 dark:text-gray-1250">
          {t('profiles.importKeycloak.empty')}
        </div>
      )
    }

    return (
      <KeycloakImportResultsTable
        items={state.items}
        importingIds={importingIds}
        importStatusById={importStatusById}
        onImport={onImport}
      />
    )
  }, [importStatusById, importingIds, onImport, state, t])

  return (
    <ModalWrapper
      classNames={{ modal: 'modal modal-keycloak-import' }}
      onClose={onClose}
      open={open}
      showCloseIcon={false}
    >
      <ModalHeader>{t('profiles.importKeycloak.title')}</ModalHeader>

      <ModalBody className="mx-[30px] my-[20px] h-full">
        <div className="flex flex-col gap-4 h-full min-h-0">
          <KeycloakImportSearchForm
            value={searchInput}
            onChange={setSearchInput}
            searchType={searchType}
            onSearchTypeChange={setSearchType}
            onSearch={onSearch}
            disabled={!canSearch || state.kind === 'loading'}
            isLoading={state.kind === 'loading'}
          />

          <div className="flex-1 min-h-0 overflow-hidden">{content}</div>

          <div className="text-right pt-2">
            <button className="small-btn cancel text-[14px]" onClick={onClose}>
              {t('global.form.button.close')}
            </button>
          </div>
        </div>
      </ModalBody>
    </ModalWrapper>
  )
}
